import { Box, Button, Modal, TextField } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useGetCompanyCategories,
  useGetCompanyDetails,
  useGetCompanyIndustries,
} from "services/company";
import { useEditClient } from "../service/ClientManagementService";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import { AddOutlined } from "@mui/icons-material";

const CreateEditRecipientModal = ({
  modalProps = {},
  id,
  onClose,
  recipientId,
}) => {
  const { setMessage } = useSnackbar();

  const [formData, setFormData] = useState({});
  const [emailRecipients, setEmailRecipients] = useState([undefined]);

  const { response: companyDetail, loading: loadingDetail } =
    useGetCompanyDetails({ id });
  const { response: industriesResp } = useGetCompanyIndustries();
  const { response: categoriesResp } = useGetCompanyCategories();
  const { mutate: updateClient, loading: loadingUpdate } = useEditClient({
    id,
  });

  const isEdit = useMemo(() => recipientId !== undefined, [recipientId]);

  const industryOptions = useMemo(
    () =>
      industriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [industriesResp],
  );

  const categoryOptions = useMemo(
    () =>
      categoriesResp?.data?.map((el) => ({
        label: el?.name,
        value: Number(el?.id),
      })),
    [categoriesResp],
  );

  const handleSubmitForm = useCallback(async () => {
    if (!isEdit) {
      const modifiedFormData = {
        ...formData,
        email_recipients: formData?.email_recipients?.filter((el) =>
          Boolean(el),
        ),
      };
      const response = await updateClient(modifiedFormData);
      if (
        response?.info?.description === "Company is updated successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully created new recipients.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to create recipients. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    } else {
      // manual validate when edit with empty value
      if (!formData?.email_recipients?.[0]) {
        return setMessage({
          desc: "Email Address cannot be blank",
          type: "error",
        });
      }

      const emailRecipients = [
        ...(companyDetail?.data?.email_recipients || []),
      ];
      emailRecipients[recipientId] = formData?.email_recipients?.[0];

      const modifiedFormData = {
        ...formData,
        email_recipients: [...emailRecipients],
      };

      const response = await updateClient(modifiedFormData);
      if (
        response?.info?.description === "Company is updated successfully" ||
        response?.info?.title === "success"
      ) {
        setMessage({
          desc: "Successfully updated recipient.",
          type: "success",
        });
        onClose();
      } else {
        const errMessage =
          response ?? "Failed to update recipient. Please try again.";
        setMessage({
          desc: errMessage,
          type: "error",
        });
      }
    }
  }, [
    isEdit,
    formData,
    updateClient,
    setMessage,
    onClose,
    companyDetail?.data?.email_recipients,
    recipientId,
  ]);

  const handleOnChangeFormInput = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddEmailRecipient = () => {
    setEmailRecipients((prev) => [...prev, undefined]);
    setFormData((prev) => ({
      ...prev,
      email_recipients: [
        ...(prev?.email_recipients?.length
          ? prev?.email_recipients
          : [undefined]),
        undefined,
      ],
    }));
  };

  const dynamicForm = useMemo(() => {
    return !isEdit ? (
      emailRecipients?.map((_el, idx) => (
        <div
          className='flex felx-row gap-3 items-center'
          key={`email-recipients-${idx}`}
        >
          <p className='text-[#5F737F] text-xs'>{`${idx < 9 ? "0" : ""}${
            idx + 1
          }`}</p>
          <TextField
            className='w-[486px]'
            placeholder='Insert e-mail address'
            value={formData?.["email_recipients"]?.[idx]}
            onChange={({ target: { value } } = {}) => {
              const newEmailRecipients = [...formData?.["email_recipients"]];
              newEmailRecipients[idx] = value;
              handleOnChangeFormInput("email_recipients", newEmailRecipients);
            }}
          />
          {idx < 9 && idx === emailRecipients?.length - 1 && !isEdit && (
            <div
              className='flex flex-row gap-2 cursor-pointer items-center'
              onClick={handleAddEmailRecipient}
            >
              <AddOutlined />
              <p className='text-[#5F737F] text-xs'>Add new email</p>
            </div>
          )}
        </div>
      ))
    ) : (
      <div className='flex felx-row gap-3 items-center'>
        <p className='text-[#5F737F] text-xs'>01</p>
        <TextField
          className='w-[486px]'
          placeholder='Insert e-mail address'
          value={formData?.["email_recipients"]?.[0]}
          onChange={({ target: { value } } = {}) =>
            handleOnChangeFormInput("email_recipients", [value])
          }
        />
        {!isEdit && (
          <div
            className='flex flex-row gap-2 cursor-pointer items-center'
            onClick={handleAddEmailRecipient}
          >
            <AddOutlined />
            <p className='text-[#5F737F] text-xs'>Add new email</p>
          </div>
        )}
      </div>
    );
  }, [emailRecipients, formData, isEdit]);

  // auto fill form when edit modal is opened
  useEffect(() => {
    if (companyDetail) {
      const clientDetail = companyDetail?.data || {};
      setFormData({
        name: clientDetail?.name,
        effect_code: clientDetail?.effect_code,
        subscription_type: clientDetail?.subscription_type,
        email_recipients: isEdit
          ? [clientDetail?.email_recipients?.[recipientId]]
          : clientDetail?.email_recipients,
        year_established: Number(
          clientDetail?.year_established || dayjs(Date.now())?.format("YYYY"),
        ),
        industry: industryOptions?.find(
          (el) => el?.label === clientDetail?.industry_name,
        )?.value,
        category: categoryOptions?.find(
          (el) => el?.label === clientDetail?.category_name,
        )?.value,
        status: clientDetail?.status,
      });

      if (clientDetail?.email_recipients?.length) {
        setEmailRecipients(
          isEdit
            ? [clientDetail?.email_recipients?.[recipientId]]
            : clientDetail?.email_recipients,
        );
      }
    }
  }, [
    id,
    loadingDetail,
    companyDetail,
    industryOptions,
    categoryOptions,
    isEdit,
    recipientId,
  ]);

  return (
    <Modal {...modalProps}>
      <Box
        sx={{
          position: "absolute",
          top: "55%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          overflow: "scroll",
          bgcolor: "background.paper",
          borderRadius: "10px",
          boxShadow: 24,
          px: "20px",
          py: "25px",
        }}
      >
        <div className='create-edit-modal-content max-h-[650px] overflow-scroll'>
          <div className='font-semibold mb-12'>
            {`${isEdit ? "Edit" : "Add New"} Monthly Report Recipients`}
          </div>
          <div className='form-input mb-8'>
            <p className='form-input-label'>Email address</p>
            {dynamicForm}
          </div>

          <div className='flex gap-4 justify-end'>
            <Button
              className='!bg-[#556FB5] w-[200px] py-2 px-[17px]'
              onClick={handleSubmitForm}
              variant='contained'
              disabled={loadingUpdate}
            >
              {`${isEdit ? "Edit" : "Create"} Recipient`}
            </Button>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default CreateEditRecipientModal;
